import { useMutation, useQueryClient } from 'react-query';
import { SpaceAlertProps } from 'services/hooks/space-service';

const useUpdateSpaceAlerts = ({
  updateQuery,
  queryName,
  onSuccess,
  onError
}: {
  updateQuery: (
    uuid: string | undefined,
    updatedSpaceAlerts: SpaceAlertProps
  ) => Promise<{ message: string }>;
  queryName: string;
  onSuccess?: (response: { message: string }) => void;
  onError?: (error: Error) => void;
}) => {
  /* The query client is responsible for managing and caching the data fetched from API endpoints. It provides methods to interact with the 
  cache, such as retrieving data, updating data, and invalidating queries. */
  const queryClient = useQueryClient();

  /* The `useMutation` hook is used to create a mutation function that can be used to perform create or update operations on a Space. */
  const saveSpaceMutation = useMutation(
    async ({
      uuid,
      updatedSpaceAlerts
    }: {
      uuid: string | undefined;
      updatedSpaceAlerts: SpaceAlertProps;
    }): Promise<{ message: string }> => {
      if (uuid) {
        try {
          const data = await updateQuery(uuid, updatedSpaceAlerts);
          return data;
        } catch (error) {
          throw error;
        }
      } else {
        throw new Error('Building uuid is required to update space alerts.');
      }
    },
    {
      onSuccess: async (response) => {
        /* The below code removes specific queries from the query client's cache. */
        queryClient.removeQueries(queryName);

        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: async (error: Error) => {
        if (onError) {
          onError(error);
        }
      }
    }
  );
  return saveSpaceMutation;
};

export default useUpdateSpaceAlerts;
